import { CGProduct } from '../../types/shopify';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { SxProps } from '@mui/system';
import clxs from 'classnames';
import CardLogo from 'components/CardConfigurer/steps/stepComponents/CardLogo';
import capitalize from 'lodash/capitalize';
import React, { ForwardedRef, forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { shopifyImageForSize } from 'lib/shopifyImageSizer';
import { LAZY_IMAGE_ENABLED } from 'environment';
// import debug from 'debug';
// const d = debug('productHeroCard');
export const WIDTH_EXTRA_SMALL = 169;
export const WIDTH_SMALL = 255;
export const WIDTH_SMALL_PLUS = 302;
export const WIDTH_MEDIUM = 390;
export const WIDTH_LARGE = 525;
export const CARD_RATIO = 0.6308;

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      width: '100%',
      display: 'inline-block',
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      boxShadow: '0px 7px 7px 0px #00000040',
      // boxShadow: '4px 4px 12px 0px rgba(0, 0, 0, 0.2)',
      borderRadius: 20,
      position: 'relative',
      transition: 'transform 0.1s',
      overflow: 'hidden',
      // aspectRatio: '856 / 539.8', // Disabled until valid everywhere...
      '&::before': {
        float: 'left',
        paddingTop: '63.08%',
        content: "''",
      },
      '&::after': {
        display: 'block',
        content: "''",
        clear: 'both',
      },
    },
    img: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center center',
      zIndex: -10,
    },
    sizeAuto: {},
    sizeExtrasmall: {
      maxWidth: WIDTH_EXTRA_SMALL,
      borderRadius: WIDTH_EXTRA_SMALL / 26.25,
    },
    sizeSmall: {
      maxWidth: WIDTH_SMALL,
      borderRadius: WIDTH_SMALL / 26.25,
    },
    sizeSmallplus: {
      maxWidth: WIDTH_SMALL_PLUS,
      borderRadius: WIDTH_SMALL_PLUS / 26.25,
    },
    sizeMedium: {
      maxWidth: WIDTH_MEDIUM,
      borderRadius: WIDTH_MEDIUM / 26.25,
    },
    sizeLarge: {
      maxWidth: WIDTH_LARGE,
      borderRadius: WIDTH_LARGE / 26.25,
    },
    noShrink: {
      '&$sizeExtrasmall': {
        minWidth: WIDTH_EXTRA_SMALL,
      },
      '&$sizeSmall': {
        minWidth: WIDTH_SMALL,
      },
      '&$sizeSmallplus': {
        minWidth: WIDTH_SMALL_PLUS,
      },
      '&$sizeMedium': {
        minWidth: WIDTH_MEDIUM,
      },
      '&$sizeLarge': {
        minWidth: WIDTH_LARGE,
      },
    },
    price: {
      top: theme.spacing(2),
      left: theme.spacing(2),
      // position: 'absolute',
      '& svg': {
        // backgroundColor: 'red',
        width: '100%',
        '& text': {
          ...theme.typography.h5,
          fill: 'white',
        },
      },
    },
    lightColor: {
      '& svg': {
        '& text': {
          fill: 'black',
        },
      },
    },
    darkColor: {
      '& svg': {
        '& text': {
          fill: 'white',
        },
      },
    },
    title: {
      '& svg': {
        marginTop: '24%',
        // backgroundColor: 'rgba(255,0,0,0.2)',
        width: '100%',
        '& text': {
          ...theme.typography.h5,
          fill: 'white',
        },
      },
    },
    bottomTitle: {
      marginTop: theme.spacing(1),
      ...theme.typography.h3,
      textAlign: 'center',
    },
    noBorderRadius: {
      borderRadius: 0,
    },
    unselected: {
      opacity: 0.3,
    },
    border: {
      border: '1px solid white',
    },
    badge: {
      backgroundColor: 'black',
      borderRadius: 50,
      ...theme.typography.subtitle1,
      color: 'white',
      display: 'inline-block',
      padding: '12px 19px',
      float: 'right',
      fontWeight: 500,
      margin: theme.spacing(2),
    },
    hoverEffect: {
      transition: 'transform 0.17s',
      '&:hover': {
        transform: 'scale(0.98)',
      },
    },
  }),
  { name: 'CardMaster' },
);

export type CGCardSize = 'extrasmall' | 'small' | 'smallplus' | 'medium' | 'large' | 'auto';

export interface CardMasterProps {
  noShrink?: boolean;
  noBorderRadius?: boolean;
  size?: CGCardSize;
  showPrice?: number | boolean;
  item?: CGProduct;
  image?: string;
  noButton?: boolean;
  hasBadge?: boolean;
  className?: string;
  onClick?: any;
  toPath?: string;
  children?: React.ReactNode;
  selected?: any;
  sx?: SxProps<Theme>;
  showBottomTitle?: boolean;
  autoBorder?: boolean;
  showLogo?: boolean;
  defaultProductHandle?: string;
  hoverEffect?: boolean;
  expiryDate?: string;
  forceDarkPrice?: boolean;
}

const CardMaster = (props: CardMasterProps, ref: ForwardedRef<any>) => {
  const {
    item, // The Product...
    showPrice = false,
    noShrink = false,
    noBorderRadius = false,
    size = 'auto',
    noButton = false,
    className,
    onClick,
    toPath,
    image,
    children,
    selected = null,
    sx,
    showBottomTitle = false,
    autoBorder = true,
    showLogo,
    defaultProductHandle,
    hoverEffect = false,
    hasBadge = false,
    expiryDate,
    forceDarkPrice = false,
  } = props;
  const brightness = forceDarkPrice || item?.pd?.priceColour === 'dark' ? 1 : 0;
  const classes = useStyles();

  // @ts-ignore
  let cardImage = image || (item?.featuredImage && item?.featuredImage.src) || (item?.featured_image && item?.featured_image.url) || item?.imgUrl || undefined;
  // Image size support...
  if (cardImage && cardImage.includes('cdn.shopify.com')) {
    let maxWidth = WIDTH_LARGE;
    switch (size) {
      case 'extrasmall':
        maxWidth = WIDTH_EXTRA_SMALL;
        break;
      case 'small':
        maxWidth = WIDTH_SMALL;
        break;
      case 'smallplus':
        maxWidth = WIDTH_SMALL_PLUS;
        break;
      case 'medium':
        maxWidth = WIDTH_MEDIUM;
        break;
      case 'large':
        maxWidth = WIDTH_LARGE;
        break;
    }
    cardImage = shopifyImageForSize(cardImage, maxWidth, '', true);
  }
  const showTitle = !cardImage;

  // onClick overrides the default router link...
  const componentProp: any = noButton ? 'div' : onClick ? undefined : RouterLink;
  const path = toPath || '/product/';
  const toProp = onClick ? undefined : path + item?.handle;
  const generatedClassNames = clxs(
    classes.root,
    // @ts-ignore
    classes['size' + capitalize(size)],
    noShrink && classes.noShrink,
    noBorderRadius && classes.noBorderRadius,
    className,
    selected === false && classes.unselected,
    autoBorder && item && item.pd?.addCardBorder && classes.border,
    hoverEffect && classes.hoverEffect,
  );
  const generatedStyles = {
    backgroundImage: LAZY_IMAGE_ENABLED ? undefined : cardImage ? 'url(' + cardImage + ')' : undefined,
    backgroundColor: !cardImage ? 'rgba(255,255,255,0.48)' : 'transparent',
  };

  const badge = item?.pd?.badge;
  const cardNode =
    noButton || !item?.handle ? (
      <>
        <Box
          // @ts-ignore
          ref={ref}
          sx={sx}
          className={generatedClassNames}
          style={generatedStyles}
        >
          {LAZY_IMAGE_ENABLED && cardImage && <img src={cardImage} className={classes.img} loading="lazy" alt={item?.title} width="100%" height="100%" />}
          {children}
          {showPrice && (
            <div className={`${classes.price} ${brightness > 0.5 ? classes.lightColor : classes.darkColor}`}>
              <svg viewBox="0 0 524 100">
                <text x="30" y="70">
                  ${showPrice}
                </text>
              </svg>
            </div>
          )}
          {!showPrice && showTitle && (
            <div className={classes.title}>
              <svg viewBox="0 0 420 100">
                <text x="50%" y="50%" textAnchor="middle">
                  {item?.title}
                </text>
              </svg>
            </div>
          )}
          {showLogo && defaultProductHandle && <CardLogo productHandle={defaultProductHandle} />}
        </Box>
        {expiryDate && (
          <Stack
            sx={{
              color: '#BBBBBB',
              fontSize: '14px',
              fontWeight: 500,
              mt: -3,
              px: 4,
              width: '100%',
              maxWidth: {
                xs: WIDTH_SMALL,
                sm: WIDTH_MEDIUM,
                md: WIDTH_LARGE,
              },
              alignItems: 'flex-end',
            }}
          >
            Expires: {expiryDate}
          </Stack>
        )}
      </>
    ) : (
      <Button
        // @ts-ignore
        ref={ref}
        disabled={noButton}
        component={componentProp} // It is a link...
        to={toProp}
        onClick={onClick ? (ev: any) => onClick(ev, item) : undefined} // Give the default onClick the current item (product)
        className={generatedClassNames}
        sx={sx}
        style={generatedStyles}
      >
        {LAZY_IMAGE_ENABLED && cardImage && <img src={cardImage} className={classes.img} loading="lazy" alt={item?.title} width="100%" height="100%" />}
        {children}
        {showPrice && (
          <div className={`${classes.price} ${brightness > 0.5 ? classes.lightColor : classes.darkColor}`}>
            <svg viewBox="0 0 524 100">
              <text x="30" y="70">
                ${showPrice}
              </text>
            </svg>
          </div>
        )}
        {showTitle && (
          <div className={classes.title}>
            <svg viewBox="0 0 420 100">
              <text x="50%" y="50%" textAnchor="middle">
                {item?.title}
              </text>
            </svg>
          </div>
        )}
        {hasBadge && badge && <div className={classes.badge}>{badge}</div>}
      </Button>
    );

  return showBottomTitle ? (
    <div>
      {cardNode}
      <div className={classes.bottomTitle}>{item?.title}</div>
    </div>
  ) : (
    cardNode
  );
};

export default forwardRef<any, CardMasterProps>(CardMaster);
